import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MainLayout from '../../components/MainLayout';
import FactoryPageHeader from '../../components/factory/FactoryPageHeader';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useSystemSettings } from '../../context/SystemSettingsContext';
import { getApiBaseUrl, getHeaders, fetchFactories } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './RealtimeShotStatus.css';

/* ===== 공장 모니터링 > 실시간 샷 현황 — PNG 시안「전사 > 한국 공장 > 실시간 샷 현황」=====
 * 헤더: 전사 › [공장 ▾] › 실시간 샷 현황 + 갱신 시각 · 새로 고침. 헤더의 공장 선택과 툴바의 "공장" 셀렉트는 같은 값이다.
 * 툴바: 공장 / 라인 셀렉트, 변경셀 강조 · 변화량 표시 · 업데이트순 정렬 · 이탈만 체크, 오른쪽 "총 n대 · 이탈 m대".
 * 표: 사출기 · 제품명 · 샷카운트 · 업데이트 · 사출속도/압력/위치 · 보압압력 · 계량압력/위치 · 사출종료위치 · 온도1~5.
 *
 * 데이터: /dashboard/realtime-shots — 설비별 마지막 샷 + previousShot(직전 샷). 공장·라인 필터는 응답의 factoryName/lineName 으로 화면에서 거른다.
 * 변화 표시: 같은 설비의 직전 샷과 값이 다르면 ▲(초록)/▼(빨강). "변경셀 강조" 가 켜져 있을 때 보이고, "변화량 표시" 면 차이 값도 붙인다.
 *           첫 로딩은 백엔드 previousShot, 이후에는 폴링 사이에 샷이 바뀌면 바로 전 행과 비교한다. 정지 설비의 첫 로딩은 과거 샷끼리
 *           비교라 오해를 주므로 제외한다(기존 규칙). 새로 바뀐 칸은 몇 초간 옅게 깜빡인다.
 * 이탈: 기준값(설정·관리 한계)을 내려주는 API 가 없어, 직전 샷 대비 ±5% 이상(항목별 최소 변동폭 이상) 바뀐 칸을 이탈로 보고
 *       빨간 칸으로 칠한다 — 추정이며 툴팁·요약에 그렇게 적는다.
 * 옵션 3개(강조·변화량·업데이트순)는 기존처럼 시스템 설정에 저장하고, "이탈만" 은 화면 상태만. 스타일은 rs- 접두어. */

const COLUMNS = [
  { key: 'equipmentName', labelKey: 'v2.colEquipment', kind: 'name' },
  { key: 'craftId', labelKey: 'columns.craftId', kind: 'text' },
  { key: 'shotCount', labelKey: 'columns.shotCount', kind: 'int' },
  { key: 'receivedAt', labelKey: 'v2.colUpdate', kind: 'time' },
  { key: 'tmInjSpeed1', labelKey: 'columns.tmInjSpeed1', digits: 1, minAbs: 0.5 },
  { key: 'tmInjPress1', labelKey: 'columns.tmInjPress1', digits: 1, minAbs: 0.5 },
  { key: 'tmInjPosi1', labelKey: 'columns.tmInjPosi1', digits: 2, minAbs: 0.05 },
  { key: 'tmHoldPress1', labelKey: 'columns.tmHoldPress1', digits: 1, minAbs: 0.5 },
  { key: 'tmChargePress1', labelKey: 'columns.tmChargePress1', digits: 1, minAbs: 0.5 },
  { key: 'tmChargePosi1', labelKey: 'columns.tmChargePosi1', digits: 1, minAbs: 0.05 },
  { key: 'turnPosi', labelKey: 'columns.turnPosi', digits: 2, minAbs: 0.05 },
  { key: 'tmTemp1Current', labelKey: 'columns.tmTemp1Current', digits: 1, minAbs: 1 },
  { key: 'tmTemp2Current', labelKey: 'columns.tmTemp2Current', digits: 1, minAbs: 1 },
  { key: 'tmTemp3Current', labelKey: 'columns.tmTemp3Current', digits: 1, minAbs: 1 },
  { key: 'tmTemp4Current', labelKey: 'columns.tmTemp4Current', digits: 1, minAbs: 1 },
  { key: 'tmTemp5Current', labelKey: 'columns.tmTemp5Current', digits: 1, minAbs: 1 }
];
const NUMERIC_COLUMNS = COLUMNS.filter((c) => c.digits != null);
/* 변경셀 강조 대상 — 설비명을 뺀 모든 열 (구 화면과 같음: 샷카운트·업데이트 시각도 바뀌면 칠한다) */
const FLASH_COLUMNS = COLUMNS.filter((c) => c.kind !== 'name');
const DEVIATION_RATIO = 0.05;   /* 직전 샷 대비 ±5% — 추정 기준 */
const FLASH_MS = 7000;   /* 구 화면과 같은 7초 동안 칠했다가 서서히 사라진다 */

/* 마지막 샷 시각 기준 가동상태 (대시보드와 동일 기준: 10분 이내 가동 / 30분 이내 대기 / 초과 정지) */
const STATUS_RUNNING_MIN = 10;
const STATUS_IDLE_MIN = 30;
function getShotStatus(receivedAt) {
  if (!receivedAt) return 'stopped';
  const ts = new Date(receivedAt).getTime();
  if (Number.isNaN(ts)) return 'stopped';
  const ageMin = (Date.now() - ts) / 60000;
  if (ageMin <= STATUS_RUNNING_MIN) return 'running';
  if (ageMin <= STATUS_IDLE_MIN) return 'idle';
  return 'stopped';
}

function getRelativeTime(receivedAt, language) {
  if (!receivedAt) return '—';
  const ts = new Date(receivedAt).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.round((ts - Date.now()) / 1000);
  const absSec = Math.abs(diffSec);
  let rtf;
  try { rtf = new Intl.RelativeTimeFormat(language || 'ko', { numeric: 'auto' }); } catch (e) { rtf = new Intl.RelativeTimeFormat('ko', { numeric: 'auto' }); }
  if (absSec < 60) return rtf.format(diffSec, 'second');
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (absSec < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

const pad2 = (n) => String(n).padStart(2, '0');
const clock = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '—' : `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};
const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));

function RealtimeShotStatus() {
  const { t, language } = useTranslation();
  const tr = useCallback((k, p) => t(`pages.monitoring.realtimeShot.${k}`, p), [t]);
  const { settings, loading: settingsLoading, refreshSettings } = useSystemSettings();

  const [rows, setRows] = useState([]);              /* [{ ...row, base }] — base: 비교 기준(직전 샷 값) */
  const [flashing, setFlashing] = useState({});      /* { 'equipmentId-key': true } */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [factories, setFactories] = useState([]);
  const [factoryId, setFactoryId] = useState(null);  /* null = 전체 */
  const [lineName, setLineName] = useState('');      /* '' = 전체 */
  const [deviationOnly, setDeviationOnly] = useState(false);
  const [options, setOptions] = useState({ highlight: true, delta: false, sortByUpdate: false });
  const [optionsInitialized, setOptionsInitialized] = useState(false);

  /* equipmentId → { shotCount, row, base } — 폴링 사이 비교 기준 */
  const compareRef = useRef(new Map());
  const flashTimerRef = useRef(null);

  useEffect(() => {
    if (!optionsInitialized && !settingsLoading && settings) {
      setOptions({
        highlight: settings.shotHighlightEnabled ?? true,
        delta: settings.shotDeltaEnabled ?? false,
        sortByUpdate: settings.shotSortByUpdate ?? false
      });
      setOptionsInitialized(true);
    }
  }, [settings, settingsLoading, optionsInitialized]);

  useEffect(() => {
    fetchFactories().then((d) => setFactories(Array.isArray(d) ? d : [])).catch((e) => console.error('공장 목록 조회 실패:', e));
    return () => clearTimeout(flashTimerRef.current);
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/dashboard/realtime-shots`, { method: 'GET', headers: getHeaders() });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'API returned success: false');
      const list = result.data || [];

      const map = compareRef.current;
      const flash = {};
      const next = list.map((row) => {
        const prev = map.get(row.equipmentId);
        let base;
        if (!prev) {
          base = getShotStatus(row.receivedAt) === 'stopped' ? null : (row.previousShot || null);
          /* 첫 로딩: 직전 샷(previousShot)과 다른 칸을 강조 — 정지 설비는 과거 샷끼리 비교라 제외 */
          if (base) {
            FLASH_COLUMNS.forEach((c) => {
              if (base[c.key] !== undefined && base[c.key] !== row[c.key]) flash[`${row.equipmentId}-${c.key}`] = true;
            });
          }
        } else {
          /* 폴링: 이전 응답과 다른 칸을 강조 (업데이트 시각·샷카운트 포함) */
          FLASH_COLUMNS.forEach((c) => {
            if (prev.row[c.key] !== row[c.key]) flash[`${row.equipmentId}-${c.key}`] = true;
          });
        }
        if (prev && prev.shotCount !== row.shotCount) {
          base = prev.row;                                     /* 새 샷 — 바로 전 행과 비교 */
        } else if (prev) {
          base = prev.base;                                    /* 같은 샷 — 기준 유지 */
        }
        map.set(row.equipmentId, { shotCount: row.shotCount, row, base });
        return { ...row, base };
      });

      setRows(next);
      setLastUpdated(new Date());
      setError(null);
      if (Object.keys(flash).length > 0) {
        setFlashing(flash);
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashing({}), FLASH_MS);
      }
    } catch (err) {
      console.error('Failed to fetch realtime shot data:', err);
      if (!silent) setError(tr('loadFailed'));
    } finally {
      if (silent) setTimeout(() => setRefreshing(false), 600); else setLoading(false);
    }
  }, [tr]);

  useEffect(() => { fetchData(false); }, [fetchData]);
  useAutoRefresh(() => fetchData(true));

  const toggleOption = async (key) => {
    const value = !options[key];
    setOptions((prev) => ({ ...prev, [key]: value }));
    const field = { highlight: 'shotHighlightEnabled', delta: 'shotDeltaEnabled', sortByUpdate: 'shotSortByUpdate' }[key];
    try {
      await fetch(`${getApiBaseUrl()}/system/settings`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ [field]: value }) });
      refreshSettings();
    } catch (err) {
      console.error('설정 저장 실패:', err);
    }
  };

  /* 칸별 변화·이탈 계산 */
  const cellInfo = useCallback((row, col) => {
    const cur = row[col.key];
    const prev = row.base ? row.base[col.key] : undefined;
    if (!isNum(cur) || !isNum(prev)) return { diff: 0, deviation: false };
    const diff = Number(cur) - Number(prev);
    if (diff === 0) return { diff: 0, deviation: false };
    const ref = Math.abs(Number(prev));
    const deviation = Math.abs(diff) >= col.minAbs && ref > 0 && Math.abs(diff) / ref >= DEVIATION_RATIO;
    return { diff, deviation };
  }, []);

  const rowDeviates = useCallback((row) => NUMERIC_COLUMNS.some((c) => cellInfo(row, c).deviation), [cellInfo]);

  const selectedFactoryName = factories.find((f) => f.id === factoryId)?.factoryName || '';
  const factoryRows = useMemo(
    () => (selectedFactoryName ? rows.filter((r) => r.factoryName === selectedFactoryName) : rows),
    [rows, selectedFactoryName]
  );
  const lineOptions = useMemo(
    () => Array.from(new Set(factoryRows.map((r) => r.lineName).filter(Boolean))).sort(),
    [factoryRows]
  );
  useEffect(() => { if (lineName && !lineOptions.includes(lineName)) setLineName(''); }, [lineOptions, lineName]);

  const scopedRows = useMemo(() => (lineName ? factoryRows.filter((r) => r.lineName === lineName) : factoryRows), [factoryRows, lineName]);
  const deviationCount = useMemo(() => scopedRows.filter(rowDeviates).length, [scopedRows, rowDeviates]);
  const visibleRows = useMemo(() => {
    let list = deviationOnly ? scopedRows.filter(rowDeviates) : scopedRows;
    if (options.sortByUpdate) list = [...list].sort((a, b) => new Date(b.receivedAt || 0) - new Date(a.receivedAt || 0));
    return list;
  }, [scopedRows, deviationOnly, rowDeviates, options.sortByUpdate]);

  const formatCell = (row, col) => {
    const v = row[col.key];
    if (col.kind === 'time') return clock(row.receivedAt);
    if (v == null || v === '') return '—';
    if (col.kind === 'int') return isNum(v) ? Number(v).toLocaleString() : String(v);
    if (col.digits != null) return isNum(v) ? Number(v).toFixed(col.digits) : String(v);
    return String(v);
  };

  const Check = ({ checked, onChange, label }) => (
    <label className={`rs-check ${checked ? 'checked' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="rs-check-box"><span className="material-symbols-outlined">check</span></span>
      <span>{label}</span>
    </label>
  );

  const renderTable = () => {
    if (loading) return <div className="rs-message">{tr('loading')}</div>;
    if (error) {
      return (
        <div className="rs-message rs-message-error">
          {error}
          <button type="button" className="rs-retry" onClick={() => fetchData(false)}>{tr('retry')}</button>
        </div>
      );
    }
    return (
      <div className="rs-table-wrap">
        <table className="rs-table">
          <thead>
            <tr>{COLUMNS.map((c) => <th key={c.key} className={c.kind === 'name' ? 'rs-sticky' : ''}>{tr(c.labelKey)}</th>)}</tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="rs-empty">{deviationOnly ? tr('v2.noDeviation') : tr('noData')}</td></tr>
            ) : visibleRows.map((row) => {
              const status = getShotStatus(row.receivedAt);
              return (
                <tr key={row.equipmentId} className={`rs-row-${status}`}>
                  {COLUMNS.map((col) => {
                    if (col.kind === 'name') {
                      const title = [row.equipmentCode, row.factoryName, row.lineName, t(`pages.monitoring.home.status.${status}`)].filter(Boolean).join(' · ');
                      return (
                        <td key={col.key} className="rs-sticky rs-name" title={title}>
                          <span className={`rs-dot rs-dot-${status}`} aria-hidden="true" />{row.equipmentName || '—'}
                        </td>
                      );
                    }
                    const flashCls = options.highlight && flashing[`${row.equipmentId}-${col.key}`] ? 'rs-flash' : '';
                    if (col.kind === 'time') {
                      return (
                        <td key={col.key} className={flashCls} title={`${getRelativeTime(row.receivedAt, language)} · ${row.receivedAt ? new Date(row.receivedAt).toLocaleString() : '—'}`}>
                          {formatCell(row, col)}
                        </td>
                      );
                    }
                    if (col.digits == null) return <td key={col.key} className={flashCls}>{formatCell(row, col)}</td>;

                    const { diff, deviation } = cellInfo(row, col);
                    const showArrow = options.highlight && diff !== 0;
                    const cls = [
                      deviation ? 'rs-dev' : '',
                      flashCls
                    ].filter(Boolean).join(' ');
                    const title = diff !== 0
                      ? `${tr('v2.prevShot')} ${isNum(row.base?.[col.key]) ? Number(row.base[col.key]).toFixed(col.digits) : '—'} → ${formatCell(row, col)}${deviation ? ` · ${tr('v2.deviationTip')}` : ''}`
                      : undefined;
                    return (
                      <td key={col.key} className={cls} title={title}>
                        <span className="rs-num">{formatCell(row, col)}</span>
                        {showArrow && (
                          <span className={`rs-arrow ${diff > 0 ? 'up' : 'down'}`}>
                            {diff > 0 ? '▲' : '▼'}{options.delta ? Math.abs(diff).toFixed(col.digits) : ''}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="rs-page">
        <FactoryPageHeader
          rootLabel={tr('v2.breadcrumbRoot')}
          factories={factories}
          selectedFactoryId={factoryId}
          onSelectFactory={(id) => setFactoryId(id)}
          allowAll
          allLabel={tr('v2.allFactories')}
          pageLabel={tr('title')}
          syncTime={lastUpdated}
          onRefresh={() => fetchData(true)}
          refreshTitle={tr('v2.refresh')}
          refreshing={refreshing}
        />

        <section className="rs-panel">
          <div className="rs-toolbar">
            <label className="rs-field">
              <span>{tr('v2.factory')}</span>
              <select className="rs-select" value={factoryId ?? ''} onChange={(e) => setFactoryId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{tr('v2.all')}</option>
                {factories.map((f) => <option key={f.id} value={f.id}>{f.factoryName}</option>)}
              </select>
            </label>
            <label className="rs-field">
              <span>{tr('v2.line')}</span>
              <select className="rs-select" value={lineName} onChange={(e) => setLineName(e.target.value)}>
                <option value="">{tr('v2.all')}</option>
                {lineOptions.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <div className="rs-checks">
              <Check checked={options.highlight} onChange={() => toggleOption('highlight')} label={tr('options.highlight')} />
              <Check checked={options.delta} onChange={() => toggleOption('delta')} label={tr('options.delta')} />
              <Check checked={options.sortByUpdate} onChange={() => toggleOption('sortByUpdate')} label={tr('options.sortByUpdate')} />
              <Check checked={deviationOnly} onChange={() => setDeviationOnly((v) => !v)} label={tr('v2.deviationOnly')} />
            </div>
            <span className="rs-summary" title={tr('v2.deviationTip')}>{tr('v2.summary', { n: scopedRows.length, d: deviationCount })}</span>
          </div>
          {renderTable()}
        </section>
      </div>
    </MainLayout>
  );
}

export default RealtimeShotStatus;
